import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const WhatsAppButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#25D366',
  color: 'white',
  '&:hover': {
    backgroundColor: '#20BA5A',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
  }
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    minWidth: '400px',
    maxWidth: '500px'
  }
}));

const ForgotPasswordDialog = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);

  const steps = ['إدخال رقم الهاتف', 'التحقق من واتساب', 'كلمة المرور الجديدة'];

  // Countdown timer
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleClose = () => {
    setActiveStep(0);
    setPhone('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setCountdown(0);
    onClose();
  };

  const formatPhoneNumber = (number) => {
    // Remove all non-digits
    const digits = number.replace(/\D/g, '');
    
    // Format as 07XXXXXXXX
    if (digits.length <= 11) {
      if (digits.startsWith('07')) {
        return digits;
      } else if (digits.startsWith('7')) {
        return '0' + digits;
      }
    }
    
    return digits.slice(0, 11);
  };

  const validatePhone = (phoneNumber) => {
    return /^(07[3-9]|075)\d{8}$/.test(phoneNumber);
  };

  const sendResetCode = async () => {
    if (!phone || !validatePhone(phone)) {
      setError('يرجى إدخال رقم هاتف عراقي صحيح (07xxxxxxxx)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://al-sadik.com'}/api/forgot_password.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'send_reset_code',
          phone: phone
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('تم إرسال رمز إعادة التعيين عبر واتساب');
        setActiveStep(1);
        setCountdown(600); // 10 minutes
      } else {
        setError(data.error || 'فشل في إرسال الرمز');
      }
    } catch (error) {
      setError('خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const verifyResetCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('يرجى إدخال رمز التحقق (6 أرقام)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://al-sadik.com'}/api/forgot_password.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'verify_reset_code',
          phone: phone,
          code: verificationCode
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('تم التحقق من الرمز بنجاح');
        setActiveStep(2);
      } else {
        setError(data.error || 'رمز التحقق غير صحيح');
      }
    } catch (error) {
      setError('خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('كلمات المرور غير متطابقة');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://al-sadik.com'}/api/forgot_password.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'reset_password',
          phone: phone,
          code: verificationCode,
          new_password: newPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('تم تغيير كلمة المرور بنجاح');
        setTimeout(() => {
          handleClose();
          onSuccess && onSuccess();
        }, 2000);
      } else {
        setError(data.error || 'فشل في تغيير كلمة المرور');
      }
    } catch (error) {
      setError('خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              أدخل رقم هاتفك لإرسال رمز إعادة تعيين كلمة المرور عبر واتساب
            </Typography>
            
            <TextField
              fullWidth
              label="رقم الهاتف"
              value={phone}
              onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
              placeholder="07xxxxxxxx"
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <WhatsAppButton
              fullWidth
              onClick={sendResetCode}
              disabled={loading || !phone}
              startIcon={loading ? <CircularProgress size={20} /> : <WhatsAppIcon />}
              sx={{ py: 1.5 }}
            >
              {loading ? 'جاري الإرسال...' : 'إرسال رمز عبر واتساب'}
            </WhatsAppButton>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              تم إرسال رمز إعادة التعيين إلى واتساب الخاص بك ({phone})
            </Typography>

            {countdown > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                الرمز صالح لمدة: {formatTime(countdown)}
              </Alert>
            )}

            <TextField
              fullWidth
              label="رمز التحقق"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              disabled={loading}
              inputProps={{ 
                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' },
                maxLength: 6
              }}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={() => setActiveStep(0)}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                رجوع
              </Button>
              <Button
                variant="contained"
                onClick={verifyResetCode}
                disabled={loading || verificationCode.length !== 6}
                sx={{ flex: 2 }}
              >
                {loading ? <CircularProgress size={20} /> : 'تحقق من الرمز'}
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              أدخل كلمة المرور الجديدة
            </Typography>

            <TextField
              fullWidth
              type="password"
              label="كلمة المرور الجديدة"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="password"
              label="تأكيد كلمة المرور"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={() => setActiveStep(1)}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                رجوع
              </Button>
              <Button
                variant="contained"
                onClick={resetPassword}
                disabled={loading || !newPassword || !confirmPassword}
                startIcon={loading ? <CircularProgress size={20} /> : <LockResetIcon />}
                sx={{ flex: 2 }}
              >
                {loading ? 'جاري التحديث...' : 'تغيير كلمة المرور'}
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <LockResetIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5">
          إعادة تعيين كلمة المرور
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            icon={<CheckCircleIcon />}
          >
            {success}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          إلغاء
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ForgotPasswordDialog;